import { useLocale, useTranslate } from '@va/localization';
import { VisitorTypes } from '@va/types/visitors';
import { Paragraph, fontWeights, paragraphSizes } from '@va/ui/design-system';
import { CloseButton, TooltipWrapper, useTooltipContext } from '@va/ui/tooltips';
import { addNumberSeparator, calculatePercentFromTotal, getPercentage, getPercentageColors } from '@va/util/helpers';
import { useMemo } from 'react';

export const VisitorsCell = ({
  newVisitors,
  returningVisitors,
  totalVisitors,
  prevTotalVisitors,
  isSegment = false,
  topParagraphSize,
}: {
  newVisitors: number;
  returningVisitors: number;
  totalVisitors: number;
  prevTotalVisitors?: number;
  isSegment?: boolean;
  topParagraphSize?: paragraphSizes;
}) => {
  const translate = useTranslate();
  const { locale } = useLocale();

  const dropdownItems = useMemo(() => {
    return [
      {
        count: returningVisitors,
        label: translate('labels.visitorTypes.returningVisitors'),
        color: window.PRIMARY_COLOR,
      },
      {
        count: newVisitors,
        label: translate('labels.visitorTypes.newVisitors'),
        color: '#2478F0',
      },
    ];
  }, [newVisitors, returningVisitors, translate]);

  const returningVisitorsPercent = useMemo(() => {
    return calculatePercentFromTotal(returningVisitors, totalVisitors);
  }, [returningVisitors, totalVisitors]);

  const percentageChangeOfTotalVisitors = useMemo(() => {
    return getPercentage(totalVisitors, prevTotalVisitors ?? 0);
  }, [prevTotalVisitors, totalVisitors]);

  return (
    <TooltipWrapper content={<VisitorsTooltip totalCount={totalVisitors} items={dropdownItems} />} interactive={true}>
      <div className='flex flex-col text-right'>
        <Paragraph
          colorClassName='text-gray-charcoal'
          weight={fontWeights.medium}
          size={topParagraphSize ?? (isSegment ? paragraphSizes.tiny : paragraphSizes.normal)}
        >
          {addNumberSeparator(totalVisitors, locale)}
        </Paragraph>

        {prevTotalVisitors !== undefined && (
          <Paragraph
            colorClassName={getPercentageColors(percentageChangeOfTotalVisitors)}
            weight={fontWeights.medium}
            size={paragraphSizes.extraTiny}
          >
            {addNumberSeparator(percentageChangeOfTotalVisitors, locale, {
              style: 'percent',
              signDisplay: 'exceptZero',
            })}
          </Paragraph>
        )}
        <div className='flex w-full gap-[0.0938rem] min-w-[4.6875rem] rounded-12 overflow-hidden'>
          <div
            className='bg-primary h-[0.2812rem] grow min-w-[0.3125rem]'
            style={{ width: `${returningVisitorsPercent}%` }}
          ></div>
          <div
            className='bg-blue-pure h-[0.2812rem] grow min-w-[0.3125rem]'
            style={{ width: `${100 - returningVisitorsPercent}%` }}
          ></div>
        </div>
      </div>
    </TooltipWrapper>
  );
};

const VisitorsTooltip = ({
  items,
  totalCount,
}: {
  items: { label: VisitorTypes; color: string; count: number }[];
  totalCount: number;
}) => {
  const { locale } = useLocale();
  const translate = useTranslate();
  const { context } = useTooltipContext();
  return (
    <div className='max-w-[21.125rem] w-screen p-2'>
      <div className='flex items-center justify-between gap-3'>
        <Paragraph weight={fontWeights.medium} size={paragraphSizes.large}>
          {translate('labels.visitors')}
        </Paragraph>
        <CloseButton onClose={() => context.onOpenChange(false)} />
      </div>
      <table className='w-full border-separate border-spacing-2'>
        <tbody className='font-medium text-15'>
          {items.map(({ label, color, count }, index) => {
            const percentage = calculatePercentFromTotal(count, totalCount);

            return (
              <tr key={index}>
                <td className='flex items-center gap-2 justify-start'>
                  <div className='w-[0.4688rem] h-[0.4688rem] rounded-full' style={{ backgroundColor: color }}></div>
                  <Paragraph weight={fontWeights.medium}>{label}</Paragraph>
                </td>
                <td className='align-middle text-center'>{addNumberSeparator(count, locale)}</td>
                <td className=' align-middle text-right'>
                  {addNumberSeparator(percentage, locale, { style: 'percent' })}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};
